import { useQuery } from 'react-query';
import { queryGet } from './fetch';
import { useLocale } from '@app/i18n';
import { useMemo } from 'react';

export interface LocalizedCountry {
  key: string;
  name: string;
  nativeName: string;
  text?: string;
  priceGroupId?: string;
}

export interface Currency {
  code: string;
  name: string;
  decimal: number;
  EURExchangeRate?: number;
  exchangeRateUpdatedAt?: Date;
}

export const useCountries = () => {
  const locale = useLocale();
  return useQuery<LocalizedCountry[]>(
    ['countries', { locale }],
    queryGet('/v2/countries', { locale }),
    {
      staleTime: 1000 * 60 * 10, // 10 min
    },
  );
};

export const useCountriesByKeys = () => {
  const { data } = useCountries();
  return useMemo(() => {
    if (!data) {
      return {};
    }

    return data.reduce((acc, country) => {
      acc[country.key] = country;
      return acc;
    }, {} as Record<string, LocalizedCountry>);
  }, [data]);
};

export const useCountriesFromKeys = (countryKeys?: string[]) => {
  const countriesByKey = useCountriesByKeys();
  return useMemo(() => {
    if (!countryKeys) {
      return [];
    }

    return countryKeys.map((key) => countriesByKey[key]).filter(Boolean);
  }, [countriesByKey, countryKeys]);
};

export const useCountryByPriceGroups = (countryKeys?: string[]) => {
  const { data } = useCountries();

  return useMemo(() => {
    const emptyRecord = {} as Record<string, string[]>;
    if (!countryKeys || !data) {
      return emptyRecord;
    }
    return data.reduce((acc, c) => {
      if (!countryKeys.includes(c.key)) {
        return acc;
      }
      const group = c.priceGroupId;
      if (!group) {
        return acc;
      }
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(c.key);
      return acc;
    }, emptyRecord);
  }, [data, countryKeys]);
};

export const usePriceGroupByCountries = () => {
  const { data } = useCountries();

  return useMemo(() => {
    const emptyRecord = {} as Record<string, string | undefined>;
    if (!data) {
      return emptyRecord;
    }
    return data.reduce((acc, c) => {
      acc[c.key] = c.priceGroupId;
      return acc;
    }, emptyRecord);
  }, [data]);
};

export const useCountryPriceTiers = (countryKeys?: string[]) => {
  const countryByPriceGroups = useCountryByPriceGroups(countryKeys);
  return useMemo(
    () => Object.keys(countryByPriceGroups),
    [countryByPriceGroups],
  );
};

export const useCurrencies = () => {
  return useQuery<Currency[]>(['currencies'], queryGet('/v2/currencies'), {
    staleTime: 1000 * 60 * 10, // 10 min
  });
};

export const useCurrencyOptions = (): Array<{
  label: string;
  value: string;
}> => {
  const { data: currencies } = useCurrencies();
  return useMemo(() => {
    if (!currencies) {
      return [];
    }
    return currencies
      .filter((c) => !!c.EURExchangeRate)
      .map((c) => ({
        label: c.code,
        value: c.code,
      }));
  }, [currencies]);
};

export const useCurrenciesMap = (): Record<string, Currency> => {
  const { data: currencies } = useCurrencies();
  return useMemo(() => {
    if (!currencies) {
      return {};
    }
    return currencies.reduce((acc, c) => {
      acc[c.code] = c;
      return acc;
    }, {} as Record<string, Currency>);
  }, [currencies]);
};
